/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
const apiRoot = process.env.API_ROOT;

export const getLink = (path: string) => `${apiRoot}${path}`;

async function parseResponse(response: Response) {
  /**
   * Instead of using response.json which might error if the body is empty, we parse as
   * text first and check it.
   */
  const text = await response.text();

  if (!text) {
    return undefined;
  }

  return await JSON.parse(text);
}

async function request(url: string, options: RequestInit) {
  const response = await fetch(url, { credentials: "include", ...options });

  if (response.status === 204) {
    return undefined;
  }

  const parsedResponse = await parseResponse(response);

  if (response.status >= 400) {
    throw parsedResponse;
  }

  return parsedResponse;
}

export function get(
  url: string,
  urlRoot?: string,
  jwt?: string,
  params?: URLSearchParams,
) {
  const headers: HeadersInit = {
    Accept: "application/json",
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  const queryString = params ? `?${params.toString()}` : "";

  return request(`${urlRoot || apiRoot}${url}${queryString}`, {
    method: "GET",
    headers,
  });
}

export function put(
  url: string,
  putData?: object,
  urlRoot?: string,
  jwt?: string,
) {
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return request(`${urlRoot || apiRoot}${url}`, {
    method: "PUT",
    headers,
    body: putData ? JSON.stringify(putData) : undefined,
  });
}

export function patch(
  url: string,
  putData?: object,
  urlRoot?: string,
  jwt?: string,
) {
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return request(`${urlRoot || apiRoot}${url}`, {
    method: "PATCH",
    headers,
    body: putData ? JSON.stringify(putData) : undefined,
  });
}

export function post(
  url: string,
  postData: object,
  urlRoot?: string,
  jwt?: string,
) {
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return request(`${urlRoot || apiRoot}${url}`, {
    headers,
    method: "POST",
    body: JSON.stringify(postData),
  });
}

export function del(
  url: string,
  urlRoot?: string,
  jwt?: string,
  payload?: object,
) {
  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (jwt) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  return request(`${urlRoot || apiRoot}${url}`, {
    method: "DELETE",
    headers,
    body: payload ? JSON.stringify(payload) : "",
  });
}
